import { useEffect, useRef } from "react";

const API_URL = process.env.REACT_APP_API_URL || "https://dev-backend.taliaz-cm.com";

/** Events the backend publishes on ai_support:{session_id}. */
export interface AISupportEvent {
  type:
    | "token_delta"
    | "tool_start"
    | "tool_end"
    | "approval_required"
    | "approval_decided"
    | "assistant_done"
    | "guidance"
    | "support_reply"
    | "walkthrough"
    | "status"
    | "escalated"
    | "error";
  session_id: number;
  audience?: "all" | "support_only";
  text?: string;
  tool?: string;
  ok?: boolean;
  action_id?: number;
  action?: string;
  arguments?: Record<string, unknown>;
  /** On `approval_required`: the asker may decide this one themselves. */
  may_approve?: boolean;
  approved?: boolean;
  decided_by?: string;
  by?: string;
  message?: string;
  pending_actions?: unknown[];
  // walkthrough payload
  title?: string;
  steps?: unknown[];
  /** The screen the guide starts on, so the overlay can offer to go there. */
  starts_at?: Record<string, unknown>;
  status?: string;
}

/**
 * Live stream for one AI Support conversation.
 *
 * Same shape as useWhatsAppSSE — token in the query string (EventSource cannot
 * set headers), 5s reconnect, handler kept in a ref so a re-render never tears
 * down the connection mid-answer.
 */
export function useAISupportSSE(
  sessionId: number | null,
  isActive: boolean,
  onEvent: (event: AISupportEvent) => void,
  /**
   * Which surface is watching, and whether it is actually on screen:
   *
   *   widget      the therapist has the panel open — this is what "live" means
   *   background  the panel is collapsed; still subscribed so an answer that
   *               arrives meanwhile is not lost, but nobody is looking
   *   console     a supporter reading over their shoulder
   *
   * Only "widget" marks the conversation live. The token cannot distinguish
   * these — a supporter is often the owner of the conversation they are
   * reading — so the client has to say.
   */
  view: "widget" | "background" | "console" = "widget"
) {
  const esRef = useRef<EventSource | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  useEffect(() => {
    if (!isActive || !sessionId) {
      return cleanup;
    }

    connect();
    return cleanup;

    function connect() {
      const token = localStorage.getItem("token");
      if (!token) return;

      const url =
        `${API_URL}/ai_support/events?token=${token}&session_id=${sessionId}&view=${view}`;
      const es = new EventSource(url);
      esRef.current = es;

      es.onmessage = (e) => {
        try {
          const event = JSON.parse(e.data) as AISupportEvent;
          if (event && event.type) {
            onEventRef.current?.(event);
          }
        } catch {
          console.error("[AISupportSSE] Failed to parse event:", e.data);
        }
      };

      es.onerror = () => {
        es.close();
        esRef.current = null;
        reconnectTimerRef.current = setTimeout(connect, 5000);
      };

      console.log("[AISupportSSE] Connection opened for session", sessionId);
    }

    function cleanup() {
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      if (esRef.current) {
        esRef.current.close();
        esRef.current = null;
        console.log("[AISupportSSE] Connection closed");
      }
    }
  }, [isActive, sessionId, view]); // eslint-disable-line react-hooks/exhaustive-deps
}
